import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { getEmptyResume } from "../components/main/mapParsedResumeToState";
import {
  clearPersistedTemplateId,
  logTemplateSelection,
  readPersistedTemplateId,
  resolveTemplateSelection,
  writePersistedTemplateId,
} from "../components/NewResume/templateSelection";

const createNoopStorage = () => ({
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
});

const COLOR_THEME_STORAGE_KEY = "resumeiq:selected-color-theme";

function readPersistedColorTheme() {
  if (typeof window === "undefined") return "blue";

  try {
    return window.localStorage.getItem(COLOR_THEME_STORAGE_KEY) || "blue";
  } catch {
    return "blue";
  }
}

function writePersistedColorTheme(colorTheme) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(COLOR_THEME_STORAGE_KEY, colorTheme || "blue");
  } catch {
    // Theme persistence should never block editing.
  }
}

export const useResumeStore = create(
  persist(
    (set, get) => ({
      resume: getEmptyResume(),
      exportLoading: false,
      selectedTemplateId: null,
      selectedTemplateSource: "unset",
      selectedColorTheme: readPersistedColorTheme(),

      setSelectedColorTheme: (colorTheme = "blue") => {
        const nextColorTheme = String(colorTheme || "blue");
        writePersistedColorTheme(nextColorTheme);
        set({ selectedColorTheme: nextColorTheme });
        return nextColorTheme;
      },

      setExportLoading: (loading) =>
        set({
          exportLoading: loading,
        }),

      setSelectedTemplateId: (templateId, source = "ui") => {
        if (!templateId) return null;

        const nextTemplateId = String(templateId);
        const currentTemplateId = get().selectedTemplateId;

        writePersistedTemplateId(nextTemplateId);

        if (
          currentTemplateId === nextTemplateId &&
          get().selectedTemplateSource === source
        ) {
          logTemplateSelection("store:setSelectedTemplateId", {
            templateId: nextTemplateId,
            source,
            persisted: true,
            skipped: true,
          });
          return nextTemplateId;
        }

        set({
          selectedTemplateId: nextTemplateId,
          selectedTemplateSource: source,
        });

        logTemplateSelection("store:setSelectedTemplateId", {
          templateId: nextTemplateId,
          source,
          persisted: true,
        });

        return nextTemplateId;
      },

      resetTemplateSelection: () => {
        clearPersistedTemplateId();
        set({
          selectedTemplateId: null,
          selectedTemplateSource: "unset",
        });
      },

      initializeSelectedTemplate: ({
        explicitTemplateId = null,
        fallbackTemplateId = null,
        allowedTemplateIds = [],
        scope = "unknown",
      } = {}) => {
        const currentTemplateId = get().selectedTemplateId;
        const currentTemplateSource = get().selectedTemplateSource;
        const persistedTemplateId = readPersistedTemplateId();
        const resolution = resolveTemplateSelection({
          explicitTemplateId,
          currentTemplateId,
          persistedTemplateId,
          fallbackTemplateId,
          allowedTemplateIds,
        });

        if (!resolution.templateId) {
          logTemplateSelection(`${scope}:init`, {
            explicitTemplateId,
            currentTemplateId,
            persistedTemplateId,
            fallbackTemplateId,
            allowedTemplateIds,
            resolvedTemplateId: null,
            source: "none",
          });
          return null;
        }

        writePersistedTemplateId(resolution.templateId);

        if (
          currentTemplateId !== resolution.templateId ||
          currentTemplateSource !== resolution.source
        ) {
          set({
            selectedTemplateId: resolution.templateId,
            selectedTemplateSource: resolution.source,
          });
        }

        logTemplateSelection(`${scope}:init`, {
          explicitTemplateId,
          currentTemplateId,
          persistedTemplateId,
          fallbackTemplateId,
          allowedTemplateIds,
          resolvedTemplateId: resolution.templateId,
          source: resolution.source,
        });

        return resolution.templateId;
      },

      setResume: (resume) =>
        set({
          resume: {
            ...getEmptyResume(),
            ...(resume || {}),
          },
        }),

      updateResume: (fieldOrUpdater, value) =>
        set((state) => {
          const currentResume = state.resume || getEmptyResume();

          if (typeof fieldOrUpdater === "function") {
            const nextResume = fieldOrUpdater(currentResume);
            return {
              resume: {
                ...currentResume,
                ...(nextResume || {}),
              },
            };
          }

          if (fieldOrUpdater && typeof fieldOrUpdater === "object") {
            return {
              resume: {
                ...currentResume,
                ...fieldOrUpdater,
              },
            };
          }

          return {
            resume: {
              ...currentResume,
              [fieldOrUpdater]: value,
            },
          };
        }),

      resetResume: () =>
        set({
          resume: getEmptyResume(),
        }),
    }),
    {
      name: "cvcanvas-resume-store",
      storage: createJSONStorage(() =>
        typeof window !== "undefined" ? window.sessionStorage  : createNoopStorage()
      ),
      partialize: (state) => ({
        resume: state.resume,
        selectedTemplateId: state.selectedTemplateId,
        selectedTemplateSource: state.selectedTemplateSource,
        selectedColorTheme: state.selectedColorTheme,
      }),
      merge: (persistedState, currentState) => {
        const persistedResume = persistedState?.resume || {};
        const persistedColorTheme =
          persistedState?.selectedColorTheme || readPersistedColorTheme();
        return {
          ...currentState,
          ...(persistedState || {}),
          resume: {
            ...getEmptyResume(),
            ...persistedResume,
          },
          selectedTemplateId: persistedState?.selectedTemplateId || null,
          selectedTemplateSource:
            persistedState?.selectedTemplateSource || "unset",
          selectedColorTheme: persistedColorTheme || "blue",
        };
      },
    }
  )
);
